import { analyzeQuestion } from "@/components/PersonalityTest/questionAnalyzer";
import { QuestionnaireFlowQuestion } from "@/types/question.types";
import { useMemo, useState } from "react";

interface QuestionWithMappedFormPaths extends QuestionnaireFlowQuestion {
  fieldPath: string;
  validationPath: string;
}

interface UseQuestionnaireFlowProps {
  questions: QuestionnaireFlowQuestion[];
  getQuestionPaths: (question: QuestionnaireFlowQuestion) => {
    fieldPath: string;
    validationPath: string;
  };
}

export function useQuestionnaireFlow({ questions, getQuestionPaths }: UseQuestionnaireFlowProps) {
  const [currentStep, setCurrentStep] = useState(0);

  const questionsWithMappedPaths = useMemo((): QuestionWithMappedFormPaths[] => {
    return questions.map((q) => {
      const { fieldPath, validationPath } = getQuestionPaths(q);
      return {
        ...q,
        fieldPath,
        validationPath,
      };
    });
  }, [questions, getQuestionPaths]);

  const maxSteps = questionsWithMappedPaths.length;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === maxSteps - 1;
  const currentQuestion = questionsWithMappedPaths[currentStep];
  const questionAnalysis = currentQuestion ? analyzeQuestion(currentQuestion) : null;
  const progressValue = maxSteps > 0 ? ((currentStep + 1) / maxSteps) * 100 : 0;

  const goToNextStep = () => {
    if (!isLastStep) {
      setCurrentStep((s) => s + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 0 && step < maxSteps) {
      setCurrentStep(step);
    }
  };

  return {
    currentStep,
    currentQuestion,
    questionAnalysis,
    maxSteps,
    isLastStep,
    progressValue,
    isFirstStep,
    actions: {
      goToNextStep,
      goToPreviousStep,
      goToStep,
    },
  };
}
